import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createDynamicCSS } from '../../../../../utils/CreateDynamicCSS';

const TOOLTIP_WIDTH = 280;
const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 6;

const posClassCache = new Set();

function positionClassForTooltip(left, top) {
  const l = Math.round(left);
  const t = Math.round(top);
  const cls = `member-ribbon-tooltip-pos-${t}-${l}`;
  if (posClassCache.has(cls)) return cls;
  posClassCache.add(cls);
  return createDynamicCSS(cls, {
    position: 'fixed',
    left: `${l}px`,
    top: `${t}px`,
    zIndex: 12050,
  });
}

export default function MemberRibbonAnchoredTooltip({
  open,
  anchorRef,
  tooltipRef,
  align = 'right',
  className,
  role,
  'aria-label': ariaLabel,
  children,
}) {
  const innerRef = useRef(null);
  const [posClass, setPosClass] = useState('');

  const setTooltipRef = useCallback(
    (node) => {
      innerRef.current = node;
      if (typeof tooltipRef === 'function') {
        tooltipRef(node);
      } else if (tooltipRef) {
        tooltipRef.current = node;
      }
    },
    [tooltipRef],
  );

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor?.getBoundingClientRect) {
      setPosClass('');
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const height = innerRef.current?.offsetHeight || 80;
    let left = align === 'left' ? rect.left : rect.right - TOOLTIP_WIDTH;
    left = Math.min(
      Math.max(left, VIEWPORT_MARGIN),
      window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
    );

    let top = rect.top - height - ANCHOR_GAP;
    if (top < VIEWPORT_MARGIN) {
      top = Math.min(
        rect.bottom + ANCHOR_GAP,
        window.innerHeight - height - VIEWPORT_MARGIN,
      );
    }

    setPosClass(positionClassForTooltip(left, top));
  }, [align, anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPosClass('');
      return undefined;
    }

    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    const onReflow = () => updatePosition();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, updatePosition, children]);

  if (!open || !posClass) return null;

  return createPortal(
    <div
      ref={setTooltipRef}
      className={[className, 'is-portaled', posClass].filter(Boolean).join(' ')}
      role={role}
      aria-label={ariaLabel}
    >
      {children}
    </div>,
    document.body,
  );
}
